"use client";

import { useRouterState } from "@tanstack/react-router";
import { useEffect, useState, useSyncExternalStore } from "react";
import { useExtracted } from "#i18n";
import styles from "./RouteProgress.module.scss";
import { isManualRouteProgressRunning, subscribeToRouteProgress } from "./RouteProgressStore";

/** Navigations quicker than this finish without showing the bar. */
const SHOW_DELAY_MS = 120;
/** How long the finished bar stays at full width before fading. */
const FINISH_MS = 300;

type Phase = "idle" | "running" | "finishing";

/**
 * A thin bar at the top of the window while a page is loading. It runs for the whole router
 * navigation, until the new route's loaders resolve, not just until the URL changes.
 */
const RouteProgress = () => {
  const translateMessage = useExtracted();
  const navigating = useRouterState({ select: (state) => state.status === "pending" });
  const manual = useSyncExternalStore(
    subscribeToRouteProgress,
    isManualRouteProgressRunning,
    () => false,
  );
  const active = navigating || manual;
  const [phase, setPhase] = useState<Phase>("idle");

  useEffect(() => {
    if (active) {
      const timer = setTimeout(() => setPhase("running"), SHOW_DELAY_MS);
      return () => clearTimeout(timer);
    }
    setPhase((current) => (current === "running" ? "finishing" : "idle"));
  }, [active]);

  useEffect(() => {
    if (phase !== "finishing") return;
    const timer = setTimeout(() => setPhase("idle"), FINISH_MS);
    return () => clearTimeout(timer);
  }, [phase]);

  if (phase === "idle") return null;
  return (
    <div
      className={styles.track}
      role="progressbar"
      aria-label={translateMessage("Loading page")}
      aria-busy={phase === "running"}
    >
      <div className={styles.bar} data-phase={phase} />
    </div>
  );
};

export default RouteProgress;
